// ============================================================================
// PATIENT TIMELINE BLOCK
// Timeline cronológico de actividad del paciente.
// - Orden estricto descendente con hora visible.
// - Filtros por tipo de evento.
// - Toggle "Ver todos los centros" (solo ADMIN).
// - Paginación con cursor ("Cargar más").
// ============================================================================
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity, FileText, CheckCircle, CreditCard, Bell, Phone,
  Building2, Paperclip, StickyNote, UserCog, Filter,
} from 'lucide-react';
import { CollapsibleSection } from '../ui/CollapsibleSection';
import { patientTimelineApi } from '../../api/patient-timeline';
import type { TimelineEvent, TimelineEventType } from '../../types/patient';
import { TIMELINE_EVENT_LABELS } from '../../types/patient';

interface Props {
  patientId: string;
  userId?: string;
  userRole: 'USER' | 'GESTOR_SEDE' | 'ADMIN';
  refreshSignal?: number;   // incrementar desde el padre para forzar reload
}

const ALL_TYPES: TimelineEventType[] = [
  'BUDGET_CREATED',
  'BUDGET_STATUS_CHANGED',
  'PAYMENT',
  'REMINDER',
  'CONTACT',
  'SITE_LINK',
  'DOCUMENT',
  'NOTE',
  'PATIENT_EDIT',
];

const ICON_MAP: Record<TimelineEventType, React.ComponentType<{ className?: string }>> = {
  BUDGET_CREATED: FileText,
  BUDGET_STATUS_CHANGED: CheckCircle,
  PAYMENT: CreditCard,
  REMINDER: Bell,
  CONTACT: Phone,
  SITE_LINK: Building2,
  DOCUMENT: Paperclip,
  NOTE: StickyNote,
  PATIENT_EDIT: UserCog,
};

const COLOR_MAP: Record<TimelineEventType, string> = {
  BUDGET_CREATED: 'bg-blue-100 text-blue-700',
  BUDGET_STATUS_CHANGED: 'bg-green-100 text-green-700',
  PAYMENT: 'bg-emerald-100 text-emerald-700',
  REMINDER: 'bg-amber-100 text-amber-700',
  CONTACT: 'bg-purple-100 text-purple-700',
  SITE_LINK: 'bg-cyan-100 text-cyan-700',
  DOCUMENT: 'bg-orange-100 text-orange-700',
  NOTE: 'bg-yellow-100 text-yellow-700',
  PATIENT_EDIT: 'bg-gray-100 text-gray-700',
};

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

function formatDayHeader(iso: string): string {
  try {
    const d = new Date(iso);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    const isToday = d.toDateString() === today.toDateString();
    const isYesterday = d.toDateString() === yesterday.toDateString();
    if (isToday) return 'Hoy';
    if (isYesterday) return 'Ayer';
    return d.toLocaleDateString('es-ES', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function dayKey(iso: string): string {
  return iso.slice(0, 10);
}

export function PatientTimelineBlock({ patientId, userId, userRole, refreshSignal = 0 }: Props) {
  const navigate = useNavigate();

  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedTypes, setSelectedTypes] = useState<Set<TimelineEventType>>(new Set(ALL_TYPES));
  const [allSites, setAllSites] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Usamos una ref para evitar race conditions con filtros rápidos
  const reqIdRef = useRef(0);

  const loadInitial = useCallback(async () => {
    reqIdRef.current += 1;
    const myReq = reqIdRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const types = selectedTypes.size === ALL_TYPES.length ? undefined : Array.from(selectedTypes);
      const result = await patientTimelineApi.getTimeline(patientId, {
        types,
        allSites: userRole === 'ADMIN' && allSites,
        limit: 30,
      });
      if (myReq !== reqIdRef.current) return; // ignora respuestas desfasadas
      setEvents(result.events);
      setHasMore(result.hasMore);
      setCursor(result.nextCursor);
    } catch (err: any) {
      if (myReq !== reqIdRef.current) return;
      setError(err?.message || 'Error al cargar el timeline');
    } finally {
      if (myReq === reqIdRef.current) setIsLoading(false);
    }
  }, [patientId, selectedTypes, allSites, userRole]);

  useEffect(() => { loadInitial(); }, [loadInitial, refreshSignal]);

  const loadMore = async () => {
    if (!cursor || !hasMore || isLoading) return;
    setIsLoading(true);
    try {
      const types = selectedTypes.size === ALL_TYPES.length ? undefined : Array.from(selectedTypes);
      const result = await patientTimelineApi.getTimeline(patientId, {
        types,
        allSites: userRole === 'ADMIN' && allSites,
        cursor,
        limit: 30,
      });
      setEvents((prev) => [...prev, ...result.events]);
      setHasMore(result.hasMore);
      setCursor(result.nextCursor);
    } catch (err: any) {
      setError(err?.message || 'Error al cargar más eventos');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleType = (t: TimelineEventType) => {
    setSelectedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
  };

  const selectAll = () => setSelectedTypes(new Set(ALL_TYPES));
  const selectNone = () => setSelectedTypes(new Set());

  const handleEventClick = (evt: TimelineEvent) => {
    if (evt.budgetId) {
      navigate(`/presupuestos/${evt.budgetId}`);
    }
  };

  // Agrupar por día para las cabeceras — orden preservado
  const groupedByDay: Array<{ day: string; events: TimelineEvent[] }> = [];
  for (const e of events) {
    const key = dayKey(e.occurredAt);
    const last = groupedByDay[groupedByDay.length - 1];
    if (last && last.day === key) {
      last.events.push(e);
    } else {
      groupedByDay.push({ day: key, events: [e] });
    }
  }

  const activeFilterCount = selectedTypes.size;

  return (
    <CollapsibleSection
      id="timeline"
      title="Timeline de actividad"
      icon={<Activity className="h-5 w-5 text-gray-400" />}
      badge={
        <span className="text-sm font-normal text-gray-500">
          {events.length} {events.length === 1 ? 'evento' : 'eventos'}
          {hasMore && '+'}
        </span>
      }
      defaultOpen={false}
      userId={userId}
      actions={
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setShowFilters((s) => !s); }}
          className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
            showFilters
              ? 'bg-[#008DD0] text-white border-[#008DD0]'
              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
          }`}
        >
          <Filter className="h-4 w-4" />
          Filtros ({activeFilterCount}/{ALL_TYPES.length})
        </button>
      }
    >
      {/* Panel de filtros */}
      {showFilters && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Tipos de evento</span>
            <div className="flex gap-2 text-xs">
              <button
                type="button"
                onClick={selectAll}
                className="text-[#008DD0] hover:underline"
              >
                Todos
              </button>
              <span className="text-gray-300">|</span>
              <button
                type="button"
                onClick={selectNone}
                className="text-[#008DD0] hover:underline"
              >
                Ninguno
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
            {ALL_TYPES.map((t) => {
              const Icon = ICON_MAP[t];
              const checked = selectedTypes.has(t);
              return (
                <label
                  key={t}
                  className={`inline-flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer border text-sm transition-colors ${
                    checked
                      ? 'bg-white border-[#008DD0] text-gray-800'
                      : 'bg-white border-gray-200 text-gray-400 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleType(t)}
                    className="sr-only"
                  />
                  <Icon className="h-4 w-4" />
                  <span>{TIMELINE_EVENT_LABELS[t]}</span>
                </label>
              );
            })}
          </div>

          {userRole === 'ADMIN' && (
            <label className="flex items-center gap-2 pt-2 border-t border-gray-200 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={allSites}
                onChange={(e) => setAllSites(e.target.checked)}
                className="rounded border-gray-300 text-[#008DD0] focus:ring-[#008DD0]"
              />
              <span>Ver actividad de todos los centros</span>
              <span className="text-xs text-gray-500">(ADMIN)</span>
            </label>
          )}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2.5 rounded-lg text-sm mb-3">
          {error}
        </div>
      )}

      {isLoading && events.length === 0 ? (
        <p className="text-sm text-gray-400">Cargando...</p>
      ) : events.length === 0 ? (
        <p className="text-sm text-gray-500 italic py-4 text-center">
          {selectedTypes.size === 0
            ? 'Selecciona al menos un tipo de evento en los filtros.'
            : 'Sin actividad registrada con los filtros seleccionados.'}
        </p>
      ) : (
        <div className="space-y-5">
          {groupedByDay.map((group) => (
            <div key={group.day}>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sticky top-0 bg-white py-1">
                {formatDayHeader(group.events[0].occurredAt)}
              </h4>
              <ol className="space-y-2">
                {group.events.map((evt) => {
                  const Icon = ICON_MAP[evt.type];
                  const colorCls = COLOR_MAP[evt.type];
                  const clickable = !!evt.budgetId;
                  return (
                    <li
                      key={evt.id}
                      className={`flex items-start gap-3 p-2 rounded-lg ${
                        clickable ? 'cursor-pointer hover:bg-gray-50' : ''
                      }`}
                      onClick={clickable ? () => handleEventClick(evt) : undefined}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${colorCls}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm text-gray-900 font-medium">{evt.title}</p>
                          <span className="text-xs text-gray-400 flex-shrink-0 font-mono">
                            {formatTime(evt.occurredAt)}
                          </span>
                        </div>
                        {evt.description && (
                          <p className="text-xs text-gray-600 mt-0.5 truncate">{evt.description}</p>
                        )}
                        <div className="flex items-center gap-2 mt-1 text-[10px] text-gray-500 flex-wrap">
                          {evt.userName && <span>{evt.userName}</span>}
                          {evt.userName && evt.siteName && allSites && <span>·</span>}
                          {evt.siteName && allSites && <span>{evt.siteName}</span>}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>
          ))}

          {hasMore && (
            <div className="text-center pt-2">
              <button
                type="button"
                onClick={loadMore}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-[#008DD0] border border-[#008DD0] rounded-lg hover:bg-[#008DD0]/10 disabled:opacity-50"
              >
                {isLoading ? 'Cargando...' : 'Cargar 30 más'}
              </button>
            </div>
          )}
        </div>
      )}
    </CollapsibleSection>
  );
}

export default PatientTimelineBlock;
